import {
    type NodeCfg,
} from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { getUID } from '@redviking/argonaut-util/src/getUID';
import type { UiNodeSpec } from './UiNodeSpec';
import { createNodeSocket } from '../../sync-utils/create-node-socket';
import { accessor } from '@redviking/argonaut-core-ui/src/store';
import type { Latest } from '@redviking/argonaut-util/types/mes/applet-designs/appletDesign.latest.zod';

export const nextTabUiNodeSpec: UiNodeSpec<'nextTab'> = {
    nodeCategory: 'tab',
    kindLabel: 'Activate Next Tab',
    requiredSockets: [ 'trigger' ],
    controls: getNodeCfg => {
        const ret: ReturnType<UiNodeSpec<'nextTab'>['controls']> = {
            tabGroupId: {
                nodeCfg: getNodeCfg(),
                value: getNodeCfg().controlValues.tabGroupId,
                cfg: {
                    useSync: false,
                    kind: 'selectTabGroup',
                    getValue: n => n.kind === 'nextTab' ? n.controlValues.tabGroupId : null,
                    onSelect: tabGroupId => {
                        const nodeCfg = getNodeCfg();
                        createNodeSocket({
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    tabGroupId,
                                },
                            },
                        });
                    },
                },
            },
        };
        return ret;
    },
    cfg: () => {
        const nextTabNodeCfg: NodeCfg<'nextTab'> = {
            id: getUID(),
            kind: 'nextTab',
            controlValues: {
                tabGroupId: '',
            },
        };
        return nextTabNodeCfg;
    },
    inputs: _cfg => {
        const inputs: ReturnType<UiNodeSpec<'nextTab'>['inputs']> = {
            trigger: { socket: 'event', label: 'Trigger' },
        };
        return inputs;
    },
    outputs: () => {
        const ret: ReturnType<UiNodeSpec<'nextTab'>['outputs']> = {
            next: { socket: 'event', label: 'Next' },
            activeTabIndex: { socket: 'number', label: 'Active Tab Index' },
        };
        return ret;
    },
    validator: node => {
        const errors: Record<string, string> = {};
        if (!node.controlValues.tabGroupId) {
            errors.tabGroupId = 'Tab group is required';
        }

        const entityData = accessor.entityAsType('appletDesignVersion');
        if (!entityData) {
            throw new Error('No applet design version found in store');
        }

        let tabGroupExists = false;
        for (const screen of entityData.config.screens) {
            for (const untypedAspectRatio in screen.gridLayoutAspectRatios) {
                const typedAspectRatio = untypedAspectRatio as Latest.Screen.Designer.GridLayoutAspectRatio;
                const screenGridCells = screen.gridLayoutAspectRatios[typedAspectRatio].linkedGridCells;
                if (screenGridCells.find((gc): gc is Latest.Screen.Designer.TabGroupCell => gc.id === node.controlValues.tabGroupId && gc.type === 'tabGroup')) {
                    tabGroupExists = true;
                    break;
                }
            }
            if (tabGroupExists) {
                break;
            }
        }

        if (!tabGroupExists) {
            errors.tabGroupId = 'Tab group ID does not exist in any screen';
        }

        return Object.keys(errors).length === 0 ? true : errors;
    },
};
